import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { ArrowRight, Article } from "@phosphor-icons/react";
import ShareButton from "@/components/ShareButton";
import SmartStoryLink from "@/components/SmartStoryLink";

/**
 * FeaturedStoriesStrip — surfaces the AI-illustrated sample stories (and any
 * approved editorial content with a cover image) on the home page. Falls
 * back silently if the archive is empty so first-run installs still look
 * clean.
 */
export default function FeaturedStoriesStrip() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await http.get("/nature/stories/list/all", { params: { limit: 8 } });
        if (!alive) return;
        // Prefer stories that have a cover image so the strip looks great.
        const withCover = (data.stories || []).filter((s) => s.media?.[0]?.url);
        setItems((withCover.length >= 3 ? withCover : (data.stories || [])).slice(0, 6));
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="py-14 md:py-20 px-5 md:px-10" data-testid="home-featured-stories">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-6">
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
              <Article size={12} weight="fill" /> / Featured stories
            </div>
            <h2 className="mt-1 font-serif-display text-3xl md:text-4xl leading-tight tracking-tight text-[#151312]">
              From the living archive.
            </h2>
            <p className="mt-1 text-sm md:text-base text-[#3A3830] max-w-xl">
              Observations, memories, editorial dispatches and citizen research from every corner of the network.
            </p>
          </div>
          <Link
            to="/stories"
            data-testid="home-featured-stories-all"
            className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#151312] hover:bg-black/5"
          >
            Browse all <ArrowRight size={12} weight="bold" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <div key={s.id} className="relative group">
              <SmartStoryLink
                story={s}
                data-testid={`home-story-${s.id}`}
                className="block rounded-2xl overflow-hidden bg-white/70 border border-black/10 hover:border-[color:var(--np-brand,#B85F2A)] transition-colors flex flex-col"
              >
                {s.media?.[0]?.url ? (
                  <div className="aspect-[16/10] overflow-hidden bg-black/5">
                    <img src={s.media[0].url} alt="" loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  </div>
                ) : (
                  <div className="aspect-[16/6] bg-gradient-to-br from-[#FBF2E1] to-[#F5EBDC] border-b border-black/5 grid place-items-center">
                    <Article size={26} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]/70" />
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    {s.story_type?.replace(/_/g, " ")}{s.category ? ` · ${s.category}` : ""}
                  </div>
                  <h3 className="mt-2 font-serif-display text-lg leading-tight line-clamp-2">
                    {s.title || s.headline}
                  </h3>
                  {s.summary && <p className="mt-2 text-sm text-[#3A3830] line-clamp-3">{s.summary}</p>}
                  <div className="mt-auto pt-3 font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)]">
                    {[s.location?.city, s.location?.state, s.location?.country].filter(Boolean).join(", ")}
                  </div>
                </div>
              </SmartStoryLink>
              {/* Share sits OUTSIDE the Link so its click doesn't bubble into router navigation */}
              <div
                className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity z-10"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
              >
                <ShareButton
                  url={`${window.location.origin}/stories/${s.id}`}
                  title={s.title || s.headline || "NaturePulse story"}
                  text={s.summary || ""}
                  shortKind="story" shortId={s.id}
                  variant="icon"
                  testid={`home-story-share-${s.id}`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
